import { computed, makeAutoObservable } from 'mobx';
import { z } from 'zod';
import { Common } from 'src/AppState/Common';
import { WebsocketV1 } from 'src/AppState/websocketV1/WebsocketV1';
import { Resource } from 'src_common/common/mobx-utils/Resource';
import { AutoWeakMap } from 'src_common/common/mobx-utils/AutoWeakMap';
import addError from 'src/utils/error.jsx';
import {
    MohPopupTriggeredNotificationType,
    NotificationMohPopupTriggersChannelType,
} from 'src_server/types/notificationData';
import { ComplianceAutomationType } from 'src/api_openapi/v2/compliance/automationSchemas';
import {
    getMohPopupTriggeredNotificationEntity,
    isMohPopupTriggeredNotification,
    MOH_POPUP_TRIGGERS_CHANNEL,
} from './mohPopupTriggersNotificationUtils';

type MohPopupTriggersNotificationActionType = 'assign' | 'unassign' | 'close';

export type MohPopupTriggersSortFieldType = 'date-and-time' | 'risk-level';
export type MohPopupTriggersSortDirectionType = 'asc' | 'desc';
export type MohPopupTriggersSectionType = 'your' | 'none' | 'other';
export type MohPopupTriggersRiskLevelType = 'low' | 'medium' | 'high';
export type MohPopupTriggersRiskLevelFilterType = 'all' | MohPopupTriggersRiskLevelType;

export const MOH_POPUP_TRIGGERS_RISK_LEVEL_FILTER_OPTIONS: Array<{
    value: MohPopupTriggersRiskLevelFilterType;
    label: string;
}> = [
    { value: 'all', label: 'All' },
    { value: 'low', label: 'LOW' },
    { value: 'medium', label: 'MEDIUM' },
    { value: 'high', label: 'HIGH' },
];

const DEFAULT_RISK_LEVEL_FILTER_BY_SECTION: Record<MohPopupTriggersSectionType, MohPopupTriggersRiskLevelFilterType> = {
    your: 'all',
    none: 'all',
    other: 'all',
};

const RISK_LEVEL_RANK: Record<MohPopupTriggersRiskLevelType, number> = {
    low: 1,
    medium: 2,
    high: 3,
};

const FiniteNumberZod = z.number().finite();

const isFiniteNumber = (value: unknown): value is number => FiniteNumberZod.safeParse(value).success;

const parseRiskLevel = (notification: MohPopupTriggeredNotificationType): MohPopupTriggersRiskLevelType | null => {
    const riskLevel = notification.details.riskLevel?.trim().toLowerCase();
    if (riskLevel === 'low' || riskLevel === 'medium' || riskLevel === 'high') {
        return riskLevel;
    }

    return null;
};

const getTriggeredAtTimestamp = (notification: MohPopupTriggeredNotificationType): number | null => {
    const value = notification.details.triggeredAt ?? notification.createdAt;
    const timestamp = Date.parse(value);
    return timestamp;
};

const compareNullableNumbers = (
    left: number | null,
    right: number | null,
    direction: MohPopupTriggersSortDirectionType
): number => {
    if (left === null && right === null) {
        return 0;
    }

    if (left === null) {
        return 1;
    }

    if (right === null) {
        return -1;
    }

    return direction === 'asc' ? left - right : right - left;
};

const compareNotifications = (
    left: MohPopupTriggeredNotificationType,
    right: MohPopupTriggeredNotificationType,
    sortField: MohPopupTriggersSortFieldType,
    sortDirection: MohPopupTriggersSortDirectionType
): number => {
    if (sortField === 'risk-level') {
        const leftRank = parseRiskLevel(left);
        const rightRank = parseRiskLevel(right);
        const compared = compareNullableNumbers(
            leftRank === null ? null : RISK_LEVEL_RANK[leftRank],
            rightRank === null ? null : RISK_LEVEL_RANK[rightRank],
            sortDirection
        );
        if (compared !== 0) {
            return compared;
        }
    }

    const comparedByDate = compareNullableNumbers(
        getTriggeredAtTimestamp(left),
        getTriggeredAtTimestamp(right),
        sortField === 'date-and-time' ? sortDirection : 'desc'
    );
    if (comparedByDate !== 0) {
        return comparedByDate;
    }

    return left.id.localeCompare(right.id);
};

const applyRiskLevelFilterAndSort = (
    notifications: Array<MohPopupTriggeredNotificationType>,
    riskLevelFilter: MohPopupTriggersRiskLevelFilterType,
    sortField: MohPopupTriggersSortFieldType,
    sortDirection: MohPopupTriggersSortDirectionType
): Array<MohPopupTriggeredNotificationType> => {
    const filtered =
        riskLevelFilter === 'all'
            ? notifications
            : notifications.filter((notification) => parseRiskLevel(notification) === riskLevelFilter);

    return [...filtered].sort((left, right) => compareNotifications(left, right, sortField, sortDirection));
};

export class MohPopupTriggersTabState {
    private readonly notificationsResource: Resource<NotificationMohPopupTriggersChannelType>;
    private readonly popupsResource: Resource<Array<{ triggerId: number; title: string }>>;
    private readonly webSocketV1: WebsocketV1;
    private actionsByAutomationName = new Map<string, string>();
    private hasShownNotificationsFetchError = false;
    private hasShownPopupsFetchError = false;
    private hasShownAutomationActionsFetchError = false;
    private hasShownUpdateNotificationError = false;
    private hasShownRefreshError = false;
    public sortField: MohPopupTriggersSortFieldType = 'date-and-time';
    public sortDirection: MohPopupTriggersSortDirectionType = 'desc';
    public riskLevelFilterBySection: Record<MohPopupTriggersSectionType, MohPopupTriggersRiskLevelFilterType> = {
        ...DEFAULT_RISK_LEVEL_FILTER_BY_SECTION,
    };

    public static get = AutoWeakMap.create((common: Common) => new MohPopupTriggersTabState(common));

    private constructor(private readonly common: Common) {
        this.webSocketV1 = WebsocketV1.get(common);

        this.notificationsResource = new Resource(async (): Promise<NotificationMohPopupTriggersChannelType> => {
            const notifications = await common.trpcClient.client.notification.getMohPopupTriggerNotifications.query();

            return notifications;
        });
        this.popupsResource = new Resource(async (): Promise<Array<{ triggerId: number; title: string }>> => {
            try {
                const popupsResponse = await common.trpcClient.client.markersOfHarmPopup.getPopups.query();
                if (popupsResponse.status !== 200) {
                    console.error('MohPopupTriggersTabState popups fetch error', popupsResponse);
                    this.showPopupsFetchError();
                    return [];
                }

                this.hasShownPopupsFetchError = false;
                return popupsResponse.body.map((popup) => ({
                    triggerId: popup.trigger_id,
                    title: popup.title,
                }));
            } catch (error) {
                console.error('MohPopupTriggersTabState popups fetch exception', error);
                this.showPopupsFetchError();
                return [];
            }
        });

        const refresh = (): void => {
            this.refresh().catch((error) => {
                console.error('MohPopupTriggersTabState refresh error', error);
                this.showRefreshError();
            });
        };

        this.webSocketV1.subscribe(`S:Notification:${MOH_POPUP_TRIGGERS_CHANNEL}`, refresh);
        makeAutoObservable(this);
    }

    private showPopupsFetchError = (): void => {
        if (this.hasShownPopupsFetchError) {
            return;
        }

        this.hasShownPopupsFetchError = true;
        addError('Failed to fetch Markers of Harm pop-up titles');
    };

    private showNotificationsFetchError = (): void => {
        if (this.hasShownNotificationsFetchError) {
            return;
        }

        this.hasShownNotificationsFetchError = true;
        addError('Failed to fetch MoH pop-up trigger notifications');
    };

    private showAutomationActionsFetchError = (): void => {
        if (this.hasShownAutomationActionsFetchError) {
            return;
        }

        this.hasShownAutomationActionsFetchError = true;
        addError('Failed to fetch automation actions for MoH pop-up triggers');
    };

    private showUpdateNotificationError = (): void => {
        if (this.hasShownUpdateNotificationError) {
            return;
        }

        this.hasShownUpdateNotificationError = true;
        addError('Failed to update MoH pop-up trigger notification');
    };

    private showRefreshError = (): void => {
        if (this.hasShownRefreshError) {
            return;
        }

        this.hasShownRefreshError = true;
        addError('Failed to refresh MoH pop-up triggers data');
    };

    @computed private get userId(): number | undefined {
        return this.common.session.userId ?? undefined;
    }

    @computed public get isLoading(): boolean {
        return this.notificationsResource.get().type === 'loading';
    }

    @computed public get hasError(): boolean {
        return this.notificationsResource.get().type === 'error';
    }

    @computed public get visibleNotifications(): Array<MohPopupTriggeredNotificationType> {
        const notifications = this.notificationsResource.getReady();
        if (notifications === null) {
            return [];
        }

        const mohPopupTriggeredNotifications: Array<MohPopupTriggeredNotificationType> = [];
        for (const notification of notifications) {
            if (isMohPopupTriggeredNotification(notification)) {
                mohPopupTriggeredNotifications.push(notification);
            }
        }

        return mohPopupTriggeredNotifications;
    }

    @computed private get popupTitleByTriggerId(): Map<number, string> {
        const popups = this.popupsResource.getReady();
        if (popups === null) {
            return new Map();
        }

        return new Map(popups.map((popup) => [popup.triggerId, popup.title]));
    }

    public getPopupTitleForTriggerId = (triggerId: number | undefined): string | null => {
        if (triggerId === undefined) {
            return null;
        }

        return this.popupTitleByTriggerId.get(triggerId) ?? null;
    };

    public getAppliedActionForPopupName = (popupName: string | undefined): string => {
        if (popupName === undefined || popupName.trim().length === 0) {
            return '-';
        }

        return this.actionsByAutomationName.get(popupName) ?? '-';
    };

    public setRiskLevelFilter = (
        section: MohPopupTriggersSectionType,
        filter: MohPopupTriggersRiskLevelFilterType
    ): void => {
        this.riskLevelFilterBySection = {
            ...this.riskLevelFilterBySection,
            [section]: filter,
        };
    };

    public getRiskLevelFilter = (section: MohPopupTriggersSectionType): MohPopupTriggersRiskLevelFilterType => {
        return this.riskLevelFilterBySection[section];
    };

    public setSortField = (field: MohPopupTriggersSortFieldType): void => {
        if (this.sortField === field) {
            this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
            return;
        }

        this.sortField = field;
        this.sortDirection = 'desc';
    };

    private getFilteredAndSortedNotifications = (
        notifications: Array<MohPopupTriggeredNotificationType>,
        section: MohPopupTriggersSectionType
    ): Array<MohPopupTriggeredNotificationType> => {
        return applyRiskLevelFilterAndSort(
            notifications,
            this.riskLevelFilterBySection[section],
            this.sortField,
            this.sortDirection
        );
    };

    @computed public get yourNotifications(): Array<MohPopupTriggeredNotificationType> {
        const userId = this.userId;
        if (userId === undefined) {
            return [];
        }

        return this.getFilteredAndSortedNotifications(
            this.visibleNotifications.filter((notification) => notification.assignee?.id === userId),
            'your'
        );
    }

    @computed public get noneNotifications(): Array<MohPopupTriggeredNotificationType> {
        return this.getFilteredAndSortedNotifications(
            this.visibleNotifications.filter(
                (notification) => notification.assignee?.id == null && notification.status !== 'assigned'
            ),
            'none'
        );
    }

    @computed public get otherNotifications(): Array<MohPopupTriggeredNotificationType> {
        const userId = this.userId;
        if (userId === undefined) {
            return [];
        }

        return this.getFilteredAndSortedNotifications(
            this.visibleNotifications.filter((notification) => {
                const assigneeId = notification.assignee?.id;
                return assigneeId !== undefined && assigneeId !== userId;
            }),
            'other'
        );
    }

    public refresh = async (): Promise<void> => {
        await Promise.all([this.notificationsResource.refresh(), this.popupsResource.refresh()]);
        if (this.notificationsResource.get().type === 'error') {
            this.showNotificationsFetchError();
        } else {
            this.hasShownNotificationsFetchError = false;
        }

        await this.refreshAutomationActions();
        this.hasShownRefreshError = false;
    };

    private refreshAutomationActions = async (): Promise<void> => {
        const popupNames = Array.from(
            new Set(
                this.visibleNotifications
                    .map((notification) => notification.details.automationName?.trim())
                    .filter(
                        (automationName): automationName is string =>
                            automationName !== undefined && automationName.length > 0
                    )
            )
        );

        if (popupNames.length === 0) {
            this.actionsByAutomationName = new Map();
            return;
        }

        try {
            const response = await this.common.trpcClient.client.complianceAutomation.getAutomationsBulk.mutate({
                names: popupNames,
            });

            if (response.status !== 200) {
                console.error('MohPopupTriggersTabState bulk automation fetch error', response);
                this.showAutomationActionsFetchError();
                this.actionsByAutomationName = new Map();
                return;
            }

            this.hasShownAutomationActionsFetchError = false;
            this.actionsByAutomationName = new Map(
                response.body.map((automation) => [automation.name, this.getAutomationActionLabel(automation)])
            );
        } catch (error) {
            console.error('MohPopupTriggersTabState bulk automation fetch exception', error);
            this.showAutomationActionsFetchError();
            this.actionsByAutomationName = new Map();
        }
    };

    private getAutomationActionLabel = (automation: ComplianceAutomationType): string => {
        const actionLabels = automation.actions
            .filter((action) => action.enabled && action.actionType !== 'ShowPopup')
            .map((action) => {
                if (action.actionType !== 'SetAccountStatus') {
                    return action.actionType;
                }

                const statusToSet = action.config['status-to-set'];
                if (typeof statusToSet === 'string') {
                    if (statusToSet === 'compliance-restriction') {
                        const duration = action.config['suspension-duration'];
                        if (isFiniteNumber(duration)) {
                            return `Compliance restriction (${duration} days)`;
                        }

                        return 'Compliance restriction';
                    }

                    if (statusToSet === 'deposit-restriction') {
                        if (action.config.permament === true) {
                            return 'Deposit restriction (permanent)';
                        }

                        const duration = action.config['suspension-duration'];
                        if (isFiniteNumber(duration)) {
                            return `Deposit restriction (${duration} hours)`;
                        }

                        return 'Deposit restriction';
                    }
                }

                return action.actionType;
            });

        if (actionLabels.length === 0) {
            return 'None';
        }

        return actionLabels.join(', ');
    };

    public updateNotification = async (
        notification: MohPopupTriggeredNotificationType,
        action: MohPopupTriggersNotificationActionType
    ): Promise<void> => {
        try {
            const response = await this.common.trpcClient.client.notificationCounts.updateNotificationCounts.mutate({
                entity: getMohPopupTriggeredNotificationEntity(notification),
                action,
                id: notification.id,
            });

            if (response.responseStatus === 'success') {
                this.hasShownUpdateNotificationError = false;
            } else {
                console.error('MohPopupTriggersTabState updateNotification error', response);
                this.showUpdateNotificationError();
            }
        } catch (error) {
            console.error('MohPopupTriggersTabState updateNotification exception', error);
            this.showUpdateNotificationError();
        }

        await this.refresh();
    };
}
